import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMyRequests } from '../services/api';
import { decodeHtmlEntities } from '../utils/categoryUtils';
import {
  getRequestState,
  getQuoteCount,
  formatShortDate,
  REQUEST_STATE_COLORS,
} from '../utils/requestState';
import { formatBudgetRange } from '../utils/budgetUtils';

interface MyRequest {
  id: number;
  title: string;
  description: string;
  category: string;
  urgency: string;
  budget_min: string | null;
  budget_max: string | null;
  location: string;
  status: string;
  created_at: string;
  // /quotes/my-requests selects qr.*, so expires_at is already on the wire.
  expires_at: string | null;
  quote_count: string;
}

const MyRequests: React.FC = () => {
  const [requests, setRequests] = useState<MyRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMyRequests();
  }, []);

  const fetchMyRequests = async () => {
    try {
      const response = await getMyRequests();
      setRequests(response.data.requests);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load your requests');
    } finally {
      setLoading(false);
    }
  };

  // Replaces getStatusColor, which switched on a raw status string. It mapped
  // an 'expired' status value that the backend never writes — an expired
  // request keeps status='open' — so a request that had stopped being visible
  // to contractors still rendered as a green "open" badge here. Expiry is
  // derived from expires_at instead.

  if (loading) return <div style={styles.loading}>Loading your requests...</div>;
  if (error) return <div style={styles.error}>{error}</div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1>📋 My Quote Requests</h1>
        <Link to="/new-request" style={styles.newButton}>+ New Request</Link>
      </div>

      {requests.length === 0 ? (
        <div style={styles.empty}>
          <h3>No requests yet</h3>
          <p>Post your first request and let businesses compete for your business!</p>
          <Link to="/new-request" style={styles.newButton}>Post a Request</Link>
        </div>
      ) : (
        <div style={styles.list}>
          {requests.map((request) => {
            const state = getRequestState(request.status, request.expires_at);
            const quotes = getQuoteCount(request.quote_count);
            const c = REQUEST_STATE_COLORS[state];
            return (
            <div key={request.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <span style={{ ...styles.status, backgroundColor: c.color }}>
                  {state}
                </span>
                <span style={styles.quoteCount}>
                  💬 {quotes} quote{quotes !== 1 ? 's' : ''}
                </span>
              </div>

              {state === 'Expired' && (
                <div style={styles.stateNote}>
                  This request expired on {formatShortDate(request.expires_at)} and is no longer shown to businesses.
                  {quotes === 0 && ' It never received a quote — post it again to keep looking.'}
                </div>
              )}
              {state === 'Closed' && (
                <div style={{ ...styles.stateNote, background: '#f1f5f9', borderColor: '#e2e8f0', color: '#475569' }}>
                  This request is closed and is no longer shown to businesses.
                  {quotes === 0 && ' It never received a quote.'}
                </div>
              )}

              <h3 style={styles.title}>{request.title}</h3>
             <p style={styles.description}>{decodeHtmlEntities(request.description).substring(0, 100)}...</p>

              <div style={styles.meta}>
                <span>💰 {formatBudgetRange(request.budget_min, request.budget_max)}</span>
                <span>📍 {request.location}</span>
              </div>

              <Link to={`/request/${request.id}`} style={styles.viewButton}>
                View Details & Quotes
              </Link>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '40px',
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
  },
  newButton: {
    padding: '12px 24px',
    backgroundColor: '#27ae60',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '5px',
    fontWeight: 'bold',
  },
  loading: {
    textAlign: 'center',
    padding: '50px',
  },
  error: {
    textAlign: 'center',
    padding: '50px',
    color: '#e74c3c',
  },
  empty: {
    textAlign: 'center',
    padding: '50px',
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  card: {
    backgroundColor: 'white',
    padding: '25px',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
  },
  status: {
    padding: '5px 12px',
    borderRadius: '15px',
    color: 'white',
    fontSize: '12px',
    textTransform: 'uppercase',
  },
  quoteCount: {
    fontSize: '14px',
    color: '#7f8c8d',
  },
  stateNote: {
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    color: '#b45309',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '13px',
    fontWeight: 600,
    marginBottom: '15px',
    lineHeight: 1.5,
  },
  title: {
    marginBottom: '10px',
    color: '#2c3e50',
  },
  description: {
    color: '#7f8c8d',
    marginBottom: '15px',
  },
  meta: {
    display: 'flex',
    gap: '20px',
    marginBottom: '15px',
    color: '#7f8c8d',
    fontSize: '14px',
  },
  viewButton: {
    display: 'inline-block',
    padding: '10px 20px',
    backgroundColor: '#3498db',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '5px',
  },
};

export default MyRequests;